/**
 * Helper: decide where to route the current user after login or on a
 * default /dashboard visit.
 *
 *   - If they have BOTH a supplier profile AND events → respect their
 *     `eventdrop_view` localStorage choice (default: host). Dual-role
 *     users almost always think of themselves as hosts first; they
 *     can still reach the supplier dashboard via the "Switch view"
 *     toggle at the top of the host dashboard.
 *   - If they have ONLY a supplier profile (no events) → supplier view
 *   - Otherwise → host dashboard (`/dashboard`)
 *
 * Honours `?view=host` and `?view=supplier` as explicit overrides
 * (they also persist to localStorage so the choice sticks).
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VIEW_KEY = 'eventdrop_view'; // 'host' | 'supplier'

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

export const setPreferredView = (v) => {
  if (v === 'host' || v === 'supplier') safeSet(VIEW_KEY, v);
};

export const resolvePostAuthPath = async ({ intendedFrom, viewOverride } = {}) => {
  // Explicit destination always wins. Login page passes `location.state.from`
  // so users who started a specific flow (e.g. /supplier/onboard) land there.
  if (intendedFrom && intendedFrom !== '/dashboard') {
    return intendedFrom;
  }
  // Explicit ?view= override (also persisted)
  if (viewOverride === 'host' || viewOverride === 'supplier') {
    setPreferredView(viewOverride);
    return viewOverride === 'supplier' ? '/dashboard/supplier' : '/dashboard';
  }
  // Detect what roles this user actually has
  let hasSupplier = false;
  let hasEvents = false;
  try {
    const r = await axios.get(`${API}/suppliers/me`);
    hasSupplier = !!r?.data?.supplier?.category;
  } catch { /* not a supplier */ }
  try {
    const r = await axios.get(`${API}/events`);
    const list = Array.isArray(r?.data) ? r.data : (r?.data?.events || []);
    hasEvents = list.length > 0;
  } catch { /* no events */ }

  if (hasSupplier && hasEvents) {
    // Dual-role — respect last choice, default to host
    const stored = safeGet(VIEW_KEY);
    if (stored === 'supplier') return '/dashboard/supplier';
    return '/dashboard';
  }
  if (hasSupplier) return '/dashboard/supplier';
  return '/dashboard';
};
